import React, { CSSProperties, MutableRefObject, useRef } from 'react';
import AntdDrawer from 'antd/lib/drawer';
import { isReactText } from '../../../utils';
import { Heading } from '../../display/heading';
import { ModalHandle, trackModalClose, trackModalOpen } from '@unifocus/js-utils';

/**
 * {@docCategory Drawer}
 */
export interface DrawerProps {
   /**
    * Whether the Drawer dialog is visible or not.
    * @defaultvalue false
    */
   visible?: boolean;

   /**
    * Whether a close (x) button is visible on top right of the Drawer dialog or not.
    * @defaultvalue true
    */
   closable?: boolean;

   /**
    * Whether to unmount child components on closing drawer or not.
    * @defaultvalue false
    */
   destroyOnClose?: boolean;

   /**
    * The placement of the Drawer.
    * @defaultvalue 'right'
    */
   position?: 'left' | 'right' | 'top' | 'bottom';

   /**
    * Specify a callback that will be called when a user clicks mask, close button or Cancel button.
    */
   onClose?: () => void;

   /**
    * Callback after the animation ends when switching drawers
    */
   afterVisibleChange?: (visible: boolean) => void;

   /**
    * The footer for Drawer
    */
   footer?: JSX.Element;

   /**
    * Placement is top or bottom, height of the Drawer dialog
    */
   height?: number;

   /**
    * Width of the Drawer dialog
    */
   width?: number;

   /**
    * Whether to show mask or not
    * @defaultvalue true
    */
   mask?: boolean;

   /**
    * Clicking on the mask (area outside the Drawer) to close the Drawer or not
    * @defaultvalue true
    */
   maskClosable?: boolean;

   /**
    * The title for Drawer
    */
   title?: JSX.Element | string;

   /**
    * Style of the drawer content part
    */
   bodyStyle?: CSSProperties;

   /**
    * Style of the popup layer element
    */
   drawerStyle?: CSSProperties;

   /**
    * 	Style of the drawer footer part
    */
   footerStyle?: CSSProperties;

   /**
    * Style of the drawer header part
    */
   headerStyle?: CSSProperties;

   /**
    * Style for Drawer's mask element
    */
   maskStyle?: CSSProperties;

   /**
    * Expand drawer to take up the full screen if position is top or bottom.
    * This should only be used if the drawer is almost full screen anyway for cosmetic purposes.
    */
   fullScreen?: boolean;

   /**
    * If this is set and mask property is true, it enables analytics pageviews for the drawer.
    * Set this to the simple name of the Drawer using all lowercase with dashes to separate words.
    * For example:  approve-employee-request.
    */
   analyticsName?: string;

   /**
    * The class name of the container of the Drawer dialog.
    */
   className?: string;

   /**
    * Style of wrapper element.
    */
   style?: CSSProperties;
}

export const Drawer = (props: React.PropsWithChildren<DrawerProps>) => {

   const modalHandleRef = useRef<ModalHandle | null>(null);
   const mask = props.mask === undefined ? true : props.mask;

   const componentClassName = 'uf-drawer';
   const analyticsName = props.analyticsName;

   if (analyticsName !== undefined && mask) {
      if (props.visible === true && modalHandleRef.current === null) {
         modalHandleRef.current = trackModalOpen(getAnalyticsPath(analyticsName));
      }
   }

   let extraClassNameSize = '';
   if (props.position === 'bottom' || props.position === 'top' || props.fullScreen) {
      extraClassNameSize = componentClassName + (props.fullScreen ? "-full-screen" : '-default');
   }

   const title = props.title && isReactText(props.title) ?
                 <Heading level={4} className='uf-drawer-title'>
                    {props.title || ''}
                 </Heading> : props.title;

   return (
      <AntdDrawer
         className={`${componentClassName} ${extraClassNameSize} ${props.className || ''}`}
         style={props.style}
         placement={props.position || 'left'}
         closable={props.closable}
         destroyOnClose={props.destroyOnClose}
         visible={props.visible}
         onClose={onClose(modalHandleRef, mask, props.onClose, analyticsName)}
         width={props.width || 330}
         afterVisibleChange={props.afterVisibleChange}
         footer={props.footer}
         height={props.height}
         mask={mask}
         maskClosable={props.maskClosable}
         title={title}
         bodyStyle={props.bodyStyle}
         drawerStyle={props.drawerStyle}
         footerStyle={props.footerStyle}
         headerStyle={props.headerStyle}
         maskStyle={props.maskStyle}
      >
         {props.children}
      </AntdDrawer>
   );
};

declare type EventType = React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement | HTMLButtonElement>;

const onClose = (modalHandleRef: MutableRefObject<ModalHandle | null>, mask: boolean, onClose?: (e: EventType) => void, analyticsName?: string) => {

   if (analyticsName === undefined || !mask) {
      return onClose;
   }

   return (event: EventType) => {
      if (modalHandleRef.current !== null) {
         trackModalClose(modalHandleRef.current);
         modalHandleRef.current = null;
      }

      if (onClose) {
         onClose(event);
      }
   };
};

const getAnalyticsPath = (analyticsName: string) => {
   const uri = `${window.location.pathname}${window.location.search}${window.location.hash}`;
   const separatorChar = uri.endsWith('/') ? '' : '/';
   return `${uri}${separatorChar}modal/${analyticsName}`;
};
